import { useEffect, useRef, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import {Save, Upload, Loader2, Building2, RotateCcw} from 'lucide-react'
import { formatarCNPJ, formatarCEP, formatarTelefone } from '@/utils/protocolo'
import type { Configuracao } from '@/types'
import toast from 'react-hot-toast'

export default function ConfiguracoesPage() {
  const [config, setConfig] = useState<Configuracao | null>(null)
  const [configId, setConfigId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState({
    nomePrefeitura: '',
    cnpj: '',
    endereco: '',
    bairro: '',
    cidade: 'São José do Seridó/RN',
    cep: '',
    telefone: '',
    email: '',
    logoUrl: '',
  })

  useEffect(() => {
    lumi.entities.configuracoes.list({ limit: 1 }).then((r) => {
      const list = r.list || []
      if (list.length > 0) {
        const c = list[0] as Configuracao
        setConfigId(c._id)
        setConfig(c)
        setForm({
          nomePrefeitura: c.nomePrefeitura || '',
          cnpj: c.cnpj || '',
          endereco: c.endereco || '',
          bairro: c.bairro || '',
          cidade: c.cidade || 'São José do Seridó/RN',
          cep: c.cep || '',
          telefone: c.telefone || '',
          email: c.email || '',
          logoUrl: c.logoUrl || '',
        })
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setUploadingLogo(true)
    try {
      // SDK correto: recebe File[] array, retorna UploadResult[]
      const results = await lumi.tools.file.upload([file])
      const r = results[0] as any
      if (r?.fileUrl) {
        handleChange('logoUrl', r.fileUrl)
        toast.success('Logo enviada com sucesso!')
      } else {
        toast.error(r?.uploadError || 'Erro ao enviar logo.')
      }
    } catch (err: any) {
      console.error('Erro upload logo:', err)
      toast.error('Erro ao enviar logo. Tente novamente.')
    } finally {
      setUploadingLogo(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nomePrefeitura.trim()) {
      toast.error('Informe o nome da prefeitura.')
      return
    }
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const data = {
        ...form,
        cidade: 'São José do Seridó/RN',
        updatedAt: now,
        creator: 'sistema',
        createdAt: configId ? (config?.createdAt || now) : now,
      }
      if (configId) {
        await lumi.entities.configuracoes.update(configId, data)
      } else {
        const result = await lumi.entities.configuracoes.create(data) as any
        setConfigId(result._id)
      }
      toast.success('Configurações salvas!')
    } catch {
      toast.error('Erro ao salvar configurações.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: '#0f3b50' }} />
        <span className="text-sm text-gray-500">Carregando...</span>
      </div>
    )
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Configurações</h1>
        <p className="text-sm text-gray-500 mt-0.5">Dados institucionais utilizados nos documentos e PDFs</p>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        {/* Logo */}
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: '#0f3b50' }} />
            Logo Oficial do Município
          </h2>
          <div className="flex items-center gap-4">
            {form.logoUrl ? (
              <div className="relative">
                <img
                  src={form.logoUrl}
                  alt="Logo da Prefeitura"
                  className="w-20 h-20 object-contain rounded-2xl border border-gray-200 bg-gray-50"
                />
                <button
                  type="button"
                  onClick={() => handleChange('logoUrl', '')}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                >
                  <span className="text-xs leading-none">×</span>
                </button>
              </div>
            ) : (
              <div className="w-20 h-20 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center bg-gray-50">
                <Building2 className="w-6 h-6 text-gray-300" />
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm rounded-2xl hover:bg-gray-50 transition disabled:opacity-50"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {uploadingLogo ? 'Enviando...' : 'Selecionar Logo'}
              </button>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP</p>
            </div>
          </div>
        </div>

        {/* Dados */}
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Dados Institucionais</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome da Prefeitura <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.nomePrefeitura}
                onChange={e => handleChange('nomePrefeitura', e.target.value)}
                placeholder="Prefeitura Municipal de São José do Seridó"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">CNPJ</label>
              <input
                type="text"
                value={form.cnpj}
                onChange={e => handleChange('cnpj', formatarCNPJ(e.target.value))}
                placeholder="00.000.000/0000-00"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone</label>
              <input
                type="text"
                value={form.telefone}
                onChange={e => handleChange('telefone', formatarTelefone(e.target.value))}
                placeholder="(84) 99999-9999"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="prefeitura@sjseridorn.gov.br"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Endereço</label>
              <input
                type="text"
                value={form.endereco}
                onChange={e => handleChange('endereco', e.target.value)}
                placeholder="Rua Principal, 123"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Bairro</label>
              <input
                type="text"
                value={form.bairro}
                onChange={e => handleChange('bairro', e.target.value)}
                placeholder="Centro"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">CEP</label>
              <input
                type="text"
                value={form.cep}
                onChange={e => handleChange('cep', formatarCEP(e.target.value))}
                placeholder="59300-000"
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cidade</label>
              <input
                type="text"
                value="São José do Seridó/RN"
                readOnly
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-100 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-2xl transition"
            style={{ backgroundColor: saving ? '#6b7280' : '#0f3b50' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </form>
    </div>
  )
}
